/**
 * The re-read's pure half: what a pull may send about the schemas it holds,
 * and (below) the state that carries a re-read across runs.
 *
 * Pure, no database, no React. `entry-repo.ts` stores and reads; the rules are
 * here so `reread.test.ts` can hold them without a device.
 */
import type { RowsKept } from '@/lib/record-grant';

/**
 * The version a cached schema is given when its hidden fields moved and the
 * server must send it again (`forgetForReread`). Below 1 on purpose: the
 * server refuses any held version under 1 (BAD_SCHEMA_VERSIONS in
 * apps/server/src/routes/sync.ts), so a marker that leaked onto the wire would
 * fail loudly rather than match a real version. `sendableSchemaVersions` is
 * what keeps it off the wire.
 */
export const FORGOTTEN_SCHEMA_VERSION = 0;

/**
 * recordId -> field_set_ver as the pull request carries it.
 *
 * Only versions the server accepts: an integer of at least 1. A schema
 * forgotten for a re-read is left out, which the server reads as "not held"
 * and answers by sending it again — the same thing a missing schema gets.
 * Sent as it was, one such row failed the whole request with 400, and every
 * later pull of that business with it, since only a pull could replace it.
 */
export const sendableSchemaVersions = (
  rows: readonly { readonly recordId: string; readonly fieldSetVer: number }[],
): Record<string, number> =>
  Object.fromEntries(
    rows
      .filter((row) => Number.isInteger(row.fieldSetVer) && row.fieldSetVer >= 1)
      .map((row) => [row.recordId, row.fieldSetVer]),
  );

/**
 * The schema half of a pull request: the versions held, and the registry this
 * build draws with (Lock 4 of Section 7.2, the same negotiation the schema
 * route does).
 *
 * The pull is where the schemas a form draws come from, and it once sent no
 * registry: the server answered in the floor, so a count asked for as a
 * stepper (`number.stepper`, since registry 2) was drawn as a plain number box
 * on every phone, whatever the schema route would have said.
 *
 * `servedAt` is the registry this business's cached schemas were served for,
 * or null when that was never said. A cache served for another registry names
 * none of its versions, so the server sends every schema again, resolved for
 * this build; an unchanged field set would otherwise keep the old components
 * for as long as the record lives.
 */
export const schemaAskOf = (
  rows: readonly { readonly recordId: string; readonly fieldSetVer: number }[],
  servedAt: number | null,
  registryVersion: number,
): { readonly schemaVersions: Record<string, number>; readonly registryVersion: number } => ({
  schemaVersions: servedAt === registryVersion ? sendableSchemaVersions(rows) : {},
  registryVersion,
});

/**
 * One record this device saw for the first time on a pull that was not a fill:
 * how many live rows the server says it has, how many this device now holds
 * from the server, and whose rows the person may keep.
 */
export type SeenRecord = {
  readonly id: string;
  readonly entryCount: number;
  readonly held: number;
  readonly kept: RowsKept;
};

/**
 * Which first-seen records were GIVEN to the person rather than made since the
 * last pull, and so have rows older than the cursor that no pull will send.
 *
 * A record made since the last pull brings every row with it, so the device
 * holds as many as the catalog counts. A record given to the person holds
 * fewer: the rows written since the last pull came, the older ones did not.
 * "Did any row come" cannot tell the two apart, because a given record whose
 * entry was edited since the last pull brings that one row.
 *
 * `entryCount` counts every author's live rows, so under a narrowed reach the
 * device holds fewer whatever happened, and the count cannot tell. Read again:
 * a missed re-read hides rows for good, an extra one costs a fill.
 */
export const givenRecords = (seen: readonly SeenRecord[]): string[] =>
  seen
    .filter((record) => record.kept !== 'none' && record.entryCount > 0)
    .filter((record) => record.kept !== 'all' || record.held < record.entryCount)
    .map((record) => record.id);

/* ------------------------------------------------- across runs */

/**
 * A re-read this business owes, stored on the device until a run that
 * finishes acts on it.
 *
 * Stored, not held in memory, because of the order a page is written in: the
 * re-read is found by comparing the page's catalog with the stored one, and
 * then that page's catalog is written. A run that fails or is killed after
 * that write leaves a next run that compares the new catalog with itself and
 * finds nothing. Held in memory, a field set to None kept its old values on
 * the phone for good, and a wider reach never brought the older rows.
 *
 *   restart  start the pull again from the beginning when a run finishes
 *   seen     records seen for the first time, not yet counted (`givenRecords`)
 */
export type RereadState = { readonly restart: boolean; readonly seen: readonly string[] };

export const NO_REREAD: RereadState = { restart: false, seen: [] };

/**
 * The stored state back. Nothing stored is nothing owed. A stored value that
 * cannot be read restarts: a missed re-read hides rows for good, an extra one
 * costs a fill.
 */
export const readRereadState = (held: string | null): RereadState => {
  if (held === null) return NO_REREAD;
  try {
    const parsed = JSON.parse(held) as { restart?: unknown; seen?: unknown } | null;
    if (parsed === null || typeof parsed !== 'object') return { restart: true, seen: [] };
    const seen = Array.isArray(parsed.seen)
      ? parsed.seen.filter((one): one is string => typeof one === 'string')
      : [];
    return { restart: parsed.restart !== false, seen };
  } catch {
    return { restart: true, seen: [] };
  }
};

export const writeRereadState = (state: RereadState): string =>
  JSON.stringify({ restart: state.restart, seen: [...state.seen] });

/**
 * One page's catalog shift added to what is owed. A widened reach or a moved
 * mask restarts; a first-seen record waits to be counted. A fill notes no
 * first-seen record: it brings every row there is.
 */
export const noteShift = (
  state: RereadState,
  shift: { readonly widened: readonly string[]; readonly masked: readonly string[]; readonly firstSeen: readonly string[] },
  fill: boolean,
): RereadState => {
  const restart = state.restart || shift.widened.length > 0 || shift.masked.length > 0;
  const seen = fill ? state.seen : [...new Set([...state.seen, ...shift.firstSeen])];
  if (restart === state.restart && seen.length === state.seen.length) return state;
  return { restart, seen };
};

/** The seen records counted: a given one restarts, and none is owed any longer. */
export const settleSeen = (state: RereadState, given: readonly string[]): RereadState => ({
  restart: state.restart || given.length > 0,
  seen: [],
});
